/**
 * proposalBuilder.ts — Typed API client for Proposal Builder Admin routes
 * Mirrors backend/src/modules/admin/proposal-builder-admin.routes.ts
 *
 * All paths are under /api/v1/admin/proposal-builder/…
 * Uses the same `api` axios instance as installations.ts (auth headers injected
 * by the interceptor in frontend/src/utils/api.ts).
 */

import { api } from '../utils/api';

// ─── log6: per-proposal PDF layout toggle ────────────────────────────────────
export type ProposalRenderMode = 'structured' | 'flat';

/**
 * Flip a proposal's PDF layout (STRUCTURED grouped spec blocks vs FLAT
 * description→value list). Persists `Proposal.renderMode`; the next PDF fetch
 * re-renders server-side. Returns `{ id, renderMode }`.
 */
export async function patchProposalRenderMode(
  id: string,
  renderMode: ProposalRenderMode,
): Promise<{ id: string; renderMode: ProposalRenderMode }> {
  const res = await api.patch<{ success: boolean; data: { id: string; renderMode: ProposalRenderMode } }>(
    `/proposals/${id}/render-mode`,
    { renderMode },
  );
  return res.data.data;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ProposalCategory {
  key: string;
  displayName: string;
  hasCalculator: boolean;
}

export type SectionScope = 'shared' | 'category' | 'conditional_calculator';

export interface ProposalSection {
  /** real DB id when the row exists, else null (upsert by key/category on save) */
  id: string | null;
  key: string;
  displayName: string;
  sortOrder: number;
  scope: SectionScope;
  /** every section is now editable (the backend returns editable:true for all) */
  editable: boolean;
  /** legacy flag — kept for back-compat, no longer gates editing */
  engineDerived?: boolean;
  contentHtml: string | null;
  images: string[];
  isActive: boolean;
  type: string;
  productCategoryId: string | null;
  /** present only on the installation-gallery section: where the "Manage" button links */
  externalLink?: string | null;
  /** the installations-admin category key to pass as ?category= */
  externalLinkCategory?: string | null;
}

export interface SectionsResponse {
  data: ProposalSection[];
  category: string | null;
}

export interface CategoriesResponse {
  data: ProposalCategory[];
}

export interface PatchSectionBody {
  contentHtml?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface ReorderItem {
  id: string;
  sortOrder: number;
}

export interface ReorderBody {
  order: ReorderItem[];
}

export interface CreateSectionBody {
  slug: string;
  name: string;
  scope?: string;
  category?: string;
  contentHtml?: string;
  sortOrder?: number;
  type?: string;
}

export interface UpsertSectionBody {
  key: string;
  category: string;
  contentHtml: string;
}

// ─── API calls ───────────────────────────────────────────────────────────────

/**
 * GET /admin/proposal-builder/categories
 * Returns the 7 product categories (5 calc-backed + 2 non-calc).
 */
export async function getCategories(): Promise<ProposalCategory[]> {
  const res = await api.get<CategoriesResponse>('/admin/proposal-builder/categories');
  return res.data?.data ?? [];
}

/**
 * GET /admin/proposal-builder/sections?category=<key>
 * Returns engine sentinel rows + DB editable rows, merged and sorted by sortOrder.
 * Omit `category` to get sections for all categories.
 */
export async function getSections(category?: string): Promise<SectionsResponse> {
  const params = category ? { category } : {};
  const res = await api.get<SectionsResponse>('/admin/proposal-builder/sections', { params });
  return res.data;
}

/**
 * PATCH /admin/proposal-builder/sections/:id
 * Update contentHtml, sortOrder, or isActive on an editable DB section.
 * Returns the updated ProposalSection row.
 */
export async function patchSection(
  id: string,
  body: PatchSectionBody,
): Promise<ProposalSection> {
  const res = await api.patch<{ data: ProposalSection }>(
    `/admin/proposal-builder/sections/${id}`,
    body,
  );
  return res.data?.data;
}

/**
 * POST /admin/proposal-builder/sections/reorder
 * Bulk-update sortOrder on a set of editable sections.
 * Engine-derived sections must NOT appear in the payload.
 */
export async function reorderSections(
  order: ReorderItem[],
): Promise<{ success: boolean; updated: number }> {
  const res = await api.post<{ success: boolean; updated: number }>(
    '/admin/proposal-builder/sections/reorder',
    { order } satisfies ReorderBody,
  );
  return res.data;
}

/**
 * PATCH /admin/proposal-builder/sections  (upsert by {key, category}, no :id)
 * Used when a section has no DB id yet (default content). The backend upserts
 * the row keyed on (key, category) and returns the row with a real id.
 * NOTE: this is the no-:id PATCH route (distinct from POST /sections = create).
 * Save path: PATCH /:id when id present, else this.
 */
export async function upsertSection(
  body: UpsertSectionBody,
): Promise<ProposalSection> {
  const res = await api.patch<{ data: ProposalSection }>(
    '/admin/proposal-builder/sections',
    body,
  );
  return res.data?.data;
}

/**
 * Save a section's content: PATCH by id when present, else upsert by key+category.
 * Returns the persisted ProposalSection (now carrying a real id).
 */
export async function saveSectionContent(
  section: { id: string | null; key: string },
  category: string,
  contentHtml: string,
): Promise<ProposalSection> {
  if (section.id) {
    return patchSection(section.id, { contentHtml });
  }
  return upsertSection({ key: section.key, category, contentHtml });
}

/**
 * POST /admin/proposal-builder/sections/:id/revert
 * Restore contentHtml + sortOrder from the latest ProposalSectionSnapshot.
 * Returns the restored ProposalSection.
 */
export async function revertSection(id: string): Promise<ProposalSection> {
  const res = await api.post<{ data: ProposalSection }>(
    `/admin/proposal-builder/sections/${id}/revert`,
  );
  return res.data?.data;
}

/**
 * POST /admin/proposal-builder/sections
 * Create a new editable ProposalSection.
 */
export async function createSection(
  body: CreateSectionBody,
): Promise<ProposalSection> {
  const res = await api.post<{ data: ProposalSection }>(
    '/admin/proposal-builder/sections',
    body,
  );
  return res.data?.data;
}

/**
 * DELETE /admin/proposal-builder/sections/:id
 * Delete an editable section. Returns 422 if the slug is engine-derived.
 */
export async function deleteSection(id: string): Promise<void> {
  await api.delete(`/admin/proposal-builder/sections/${id}`);
}

/**
 * POST /admin/proposal-builder/preview
 * Returns a full styled proposal HTML string for the given category, with
 * any saved section edits applied. Render the response in a sandboxed iframe.
 */
export async function previewProposal(category: string): Promise<{ html: string }> {
  const res = await api.post<{ html: string }>('/admin/proposal-builder/preview', { category });
  return res.data;
}

/** Each keyed section's REAL default-rendered HTML for a category, so the editor
 *  can load the actual proposal markup (not a stub) when no override exists. */
export async function previewSectionHtml(
  category: string,
): Promise<{ sections: Record<string, string>; css?: string }> {
  const res = await api.post<{ sections: Record<string, string>; css?: string }>(
    '/admin/proposal-builder/section-html',
    { category },
  );
  return res.data;
}
